package gr.tech.site.pages.states

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.objectFit
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UncoloredLinkVariant
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.site.components.layouts.PageLayoutData
import gr.tech.site.components.sections.RelatedSection
import gr.tech.site.components.states.FaqAccordion
import gr.tech.site.components.states.FaqItem
import gr.tech.site.components.states.UtilityCardData
import gr.tech.site.components.states.UtilityPartnerGrid
import gr.tech.site.styles.Theme
import gr.tech.site.styles.landings.*
import gr.tech.site.styles.states.MichiganHeroTextStyle
import gr.tech.site.styles.states.StateStatIconStyle
import gr.tech.site.styles.states.StateStatLabelStyle
import gr.tech.site.styles.states.StateStatNumberStyle
import gr.tech.site.util.ImageRepo
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.*

@InitRoute
fun initMichiganPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData(
        showContactHeader = true,
        title = "Michigan - Up to 100% on LEDs, HVAC & More",
        description = "DTE Energy and Consumers Energy offer rebates on LED grow lights, HVAC, and dehumidification for Michigan commercial growers. Green Rebates maximizes every dollar back.",
        canonicalUrl = "https://greenrebates.com/states/michigan",
        ogImage = "/images/michigan1.webp"
    ))
}

@Page()
@Layout(".components.layouts.PageLayout")
@Composable
fun MichiganPage() {
    Main(
        attrs = Modifier
            .classNames("d-flex", "flex-column")
            .fillMaxSize()
            .role("main")
            .toAttrs()
    ) {

        // HERO
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(560.px)
                .position(Position.Relative)
                .classNames("px-0"),
            contentAlignment = Alignment.Center
        )
        {
            // Hero Image
            Img(
                src = ImageRepo.Michigan1.src,
                alt = ImageRepo.Michigan1.alt,
                attrs = Modifier.fillMaxSize().toAttrs{
                    attr("fetchpriority", "high") // ensures LCP optimization
                    style {
                        objectFit(ObjectFit.Cover) // preserve background-like cover
                    }
                }
            )

            Div(
                attrs = Modifier
                    .fillMaxSize()
                    .backgroundColor(Colors.Black)
                    .opacity(30.percent)
                    .position(Position.Absolute)
                    .top(0.px).left(0.px)
                    .toAttrs()
            )

            Column(
                modifier = LandingHeroStyle.toModifier().classNames("container", "text-start").fillMaxWidth(),
                horizontalAlignment = Alignment.Start
            )
            {
                H3(
                    attrs = LandingHeroEyebrowStyle.toModifier()
                        .then(MichiganHeroTextStyle.toModifier())
                        .classNames("text-uppercase", "mb-2").toAttrs()
                ) {
                    Text("Michigan Energy Rebates")
                }

                H1(
                    attrs = LandingHeroHeadlineStyle.toModifier()
                        .then(MichiganHeroTextStyle.toModifier())
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Claim Rebates And Cash on Your Horticulture Facility Upgrades")
                }

                P(
                    attrs = LandingHeroLeadStyle.toModifier()
                        .then(MichiganHeroTextStyle.toModifier())
                        .classNames("mb-4", "fw-medium", "text-white").toAttrs()
                ) {
                    Text("Green Rebates will work closely with local Michigan utilities to help growers secure up to 100% funding for LED grow lights, HVAC, and dehumidification.")
                }

                Link(
                    path = "/contact-us?state=Michigan",
                    text = "Free Rebates Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaSecondaryStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Get a free cannabis and marijuana rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }

        // HOW IT WORKS
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2").toAttrs()
                ) {
                    Text("How to get Energy Rebates in Michigan")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-3").toAttrs()
                ) {
                    Text("Three easy steps to utilize our expertise and get rebates for your facilities.")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        Triple("1", "Tell Us About Your Operation",
                            "Enter your project details so we can match you with the right equipment and appropriate rebates."),
                        Triple("2", "We Handle the Paperwork",
                            "The utility application process is difficult and tedious. We manage every step of the way from submission to approval. We'll handle the communication on your behalf."),
                        Triple("3", "You Reap The Rewards",
                            "Sit back, relax, and watch us as we help you secure up to thousands of dollars in rebates through Green Rebates.")
                    ).forEach { (num, title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(
                                attrs = Modifier
                                    .classNames("d-flex", "flex-column", "align-items-center", "text-center", "gap-3", "h-100", "py-4", "px-3")
                                    .toAttrs()
                            ) {
                                Div(
                                    attrs = LandingStepCardNumberStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center")
                                        .toAttrs()
                                ) { Text(num) }
                                H4(
                                    attrs = LandingStepCardTitleStyle.toModifier()
                                        .classNames("mb-1")
                                        .toAttrs()
                                ) { Text(title) }
                                P(
                                    attrs = LandingStepCardBodyStyle.toModifier()
                                        .classNames("mb-0")
                                        .toAttrs()
                                ) { Text(desc) }
                            }
                        }
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    Link(
                        path = "/contact-us?state=Michigan",
                        text = "Contact Us Now \u2192",
                        variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                        modifier = LandingCtaBlackOutlineStyle.toModifier().classNames("mx-auto")
                            .role("button")
                            .attrsModifier { attr("aria-label", "Contact Us") },
                        openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                        openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                    )
                }
            }
        }

        // WHY GREEN REBATES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Why Growers Choose Green Rebates")
                }

                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-sm-2", "g-4", "mb-3").toAttrs()) {
                    listOf(
                        "Maximize Every Dollar" to
                                "We research every rebate program and incentive available, including stacking opportunities, to ensure your facility receives the maximum funding possible.",
                        "Full-Service for Grow Operations" to
                                "From grow room audits to rebate submissions and checks, we manage every step of the process for your cultivation or licensed operations.",
                        "Industry Expertise" to
                                "We specialize exclusively in commercial horticulture and grow operations — not general energy consulting. We know every rebate program available for indoor and greenhouse growers.",
                        "Direct Utility Relationships" to
                                "Our direct relationships with utilities and program administrators mean faster approvals and higher rebate payouts for your facilities.",
                    ).forEach { (title, desc) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = Modifier.classNames("d-flex", "gap-3", "align-items-start").toAttrs()) {
                                Div(
                                    attrs = LandingCheckIconStyle.toModifier()
                                        .classNames("d-flex", "align-items-center", "justify-content-center", "flex-shrink-0")
                                        .toAttrs()
                                ) { FaCheck(size = IconSize.SM) }
                                Div {
                                    H5(attrs = LandingDarkItemTitleStyle.toModifier().classNames("mb-1").toAttrs()) {
                                        Text(title)
                                    }
                                    P(attrs = LandingDarkItemBodyStyle.toModifier().classNames("mb-0").toAttrs()) {
                                        Text(desc)
                                    }
                                }
                            }
                        }
                    }
                }

                P(attrs = LandingMutedWhiteBodyStyle.toModifier().classNames("text-center").toAttrs()) {
                    Text("All of this is powered by our close, trusted partnerships with utilities in Michigan.")
                }
            }
        }

        // TRUSTED UTILITIES
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "pt-5", "pb-3")
                .id("utilities")
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "pb-1").toAttrs()) {
                H2(
                    attrs = LandingSectionHeadingStyle.toModifier()
                        .classNames("mb-2")
                        .toAttrs()
                ) {
                    Text("Trusted Utility Partners")
                }
                P(
                    attrs = LandingSectionSubtitleStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("Green Rebates works closely with leading utilities to make sure your grow operation qualifies for every available rebate. Each utility program has its own requirements and incentives, and our team handles the details so you get the maximum funding possible.")
                }

                UtilityPartnerGrid(listOf(
                    UtilityCardData(utilityName = "Consumers Energy", state = "Michigan", utilityParam = "Consumers"),
                    UtilityCardData(utilityName = "DTE Energy", state = "Michigan", utilityParam = "DTE")
                ))

            }
        }

        // STATS + TESTIMONIALS SECTION
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .toAttrs()
        )
        {
            H2(
                attrs = LandingSectionHeadingStyle.toModifier()
                    .classNames("mb-2")
                    .toAttrs()
            ) {
                Text("Results That Speak")
                SpanText(
                    text = " Louder ",
                    modifier = Modifier.classNames("fw-italic")
                        .color(Theme.MediumGreen.rgb)
                )
                Text("Than Words")
            }

            P(
                attrs = LandingSectionSubtitleStyle.toModifier()
                    .classNames("mb-4")
                    .toAttrs()
            ) {
                Text("Our clients see real rebates, real savings, and real impact across the Great Lakes State.")
            }

            Div(attrs = Modifier.classNames("container", "mb-5").toAttrs()) {
                Div(
                    attrs = Modifier
                        .classNames("row", "row-cols-1", "row-cols-md-3", "g-4", "g-lg-5", "justify-content-center")
                        .toAttrs()
                ) {
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaDollarSign(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("$55M+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Total Rebates Secured") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaClipboardList(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("400+") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("Projects Completed") }
                        }
                    }
                    Div(attrs = Modifier.classNames("col", "d-flex", "flex-column", "align-items-center").toAttrs()) {
                        Div(attrs = Modifier.classNames("text-center").toAttrs()) {
                            FaBolt(modifier = StateStatIconStyle.toModifier(), size = IconSize.X2)
                            H3(attrs = StateStatNumberStyle.toModifier().toAttrs()) { Text("420M") }
                            P(attrs = StateStatLabelStyle.toModifier().toAttrs()) { Text("kWh Saved") }
                        }
                    }
                }
            }


            Div(attrs = Modifier.classNames("container").toAttrs()) {
                Div(attrs = Modifier.classNames("row", "row-cols-1", "row-cols-md-2", "g-4").toAttrs()) {
                    listOf(
                        "Green Rebates got our cannabis facility over $50,000 back on LED grow light and HVAC upgrades. The entire process was completely effortless - they handled everything for our marijuana operation." to
                                "Facility Owner",
                        "I didn't think utility rebates could cover this much for a pot grow. Green Rebates managed every application for our marijuana facility from start to finish. I'd recommend them to any cannabis operator." to
                                "Cultivator"
                    ).forEach { (quote, attribution) ->
                        Div(attrs = Modifier.classNames("col").toAttrs()) {
                            Div(attrs = LandingTestimonialCardStyle.toModifier().toAttrs()) {
                                Span(
                                    attrs = LandingQuoteMarkStyle.toModifier()
                                        .classNames("d-block", "mb-1")
                                        .toAttrs()
                                ) { Text("\u201C") }
                                P(attrs = LandingBodyStyle.toModifier().classNames("fst-italic", "mb-3").toAttrs()) {
                                    Text(quote)
                                }
                                Span(attrs = LandingAttributionStyle.toModifier().toAttrs()) {
                                    Text("- $attribution")
                                }
                            }
                        }
                    }
                }
            }
        }

        // FAQ
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .backgroundColor(Theme.DarkGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "py-5").toAttrs()) {
                H2(
                    attrs = LandingDarkSectionHeadingStyle.toModifier()
                        .classNames("mb-5")
                        .toAttrs()
                ) {
                    Text("FAQs About Rebates in Michigan")
                }

                FaqAccordion(
                    accordionId = "michigan-faq",
                    items = listOf(
                        FaqItem(
                            question = "Who is eligible for Michigan energy rebates?",
                            answer = "Licensed cannabis and marijuana cultivators in Michigan who pay utility bills to Consumers Energy or DTE Energy may qualify. Most commercial grow operations with significant lighting or HVAC loads are eligible."
                        ),
                        FaqItem(
                            question = "Do Michigan utilities offer rebates for grow lighting?",
                            answer = "Yes. Both Consumers Energy and DTE Energy offer substantial rebates for LED grow light replacements, including fixtures used in cannabis and marijuana cultivation facilities."
                        ),
                        FaqItem(
                            question = "How much can I get back in Michigan?",
                            answer = "Rebate amounts vary based on your utility, equipment type, and energy savings. Michigan cannabis growers have received anywhere from a few thousand dollars to over $100,000 in rebates through Green Rebates."
                        ),
                        FaqItem(
                            question = "Do I need pre-approval before purchasing equipment?",
                            answer = "Most Michigan utility programs require pre-approval before purchasing equipment. Green Rebates handles the entire application process — including pre-approval, documentation, and follow-up — so you don't miss out on funding."
                        )
                    )
                )
            }
        }

        RelatedSection(listOf(
            "/article/the-2026-guide-to-cannabis-rebates-how-to-get-paid-for-your-grow-upgrades" to "The 2026 Guide to Cannabis Rebates: How to Get Paid for Your Grow Upgrades",
            "/article/top-10-high-efficiency-led-rebates-for-commercial-cultivators-in-2026" to "Top 10 High-Efficiency LED Rebates for Commercial Cultivators in 2026",
            "/article/cash-back-for-growers-a-step-by-step-roadmap-to-utility-incentives" to "Cash Back for Growers: A Step-by-Step Roadmap to Utility Incentives",
        ))

        // FINAL CTA
        Section(
            attrs = Modifier
                .classNames("container-fluid", "px-0", "py-5", "py-lg-8")
                .id("contact")
                .backgroundColor(Theme.MediumGreen.rgb)
                .toAttrs()
        )
        {
            Div(attrs = Modifier.classNames("container", "text-center").toAttrs()) {
                H2(
                    attrs = LandingCtaHeadingStyle.toModifier()
                        .classNames("mb-3")
                        .toAttrs()
                ) {
                    Text("Ready to Save Money?")
                }
                P(
                    attrs = LandingCtaBodyStyle.toModifier()
                        .classNames("mb-4", "mx-auto")
                        .toAttrs()
                ) {
                    Text("What are we waiting for? Let's get you that cash. Contact us today!")
                }
                Link(
                    path = "/contact-us?state=Michigan",
                    text = "Free Rebate Estimate \u2192",
                    variant = UndecoratedLinkVariant.then(UncoloredLinkVariant),
                    modifier = LandingCtaThirdStyle.toModifier()
                        .role("button")
                        .attrsModifier { attr("aria-label", "Contact Green Rebates for a free cannabis and marijuana rebate estimate") },
                    openInternalLinksStrategy = OpenLinkStrategy.IN_PLACE,
                    openExternalLinksStrategy = OpenLinkStrategy.IN_PLACE
                )
            }
        }


    }
}
